import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem,
  Container, Avatar, Divider, useScrollTrigger, Slide, Badge, Tooltip, Chip
} from '@mui/material';
import {
  Search, Dashboard, Logout, CloudUpload,
  Login as LoginIcon, PersonAdd, ShoppingCart, Notifications,
  FavoriteBorder, Chat, LocalShipping, Bolt, TrendingUp
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { notificationAPI, cartAPI } from '../services/api';

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger({ threshold: 80 });
  return <Slide appear={false} direction="down" in={!trigger}>{children}</Slide>;
}

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 20 });

  useEffect(() => {
    if (isAuthenticated) {
      loadCounts();
      const interval = setInterval(loadCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadCounts = async () => {
    try {
      const [notifRes, cartRes] = await Promise.all([notificationAPI.unreadCount(), cartAPI.count()]);
      setUnreadCount(notifRes.data.count);
      setCartCount(cartRes.data.count);
    } catch (e) {}
  };

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); handleClose(); navigate('/'); };
  const isActive = (path) => location.pathname === path;

  const navLink = (path, label) => ({
    color: isActive(path) ? 'primary.main' : 'text.secondary',
    fontWeight: isActive(path) ? 700 : 500,
    fontSize: '0.85rem',
    px: 1.8, py: 0.7,
    borderRadius: 2.5,
    bgcolor: isActive(path) ? 'rgba(79,70,229,0.06)' : 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': { bgcolor: 'rgba(79,70,229,0.06)', color: 'primary.main' },
  });

  return (
    <HideOnScroll>
      <AppBar position="sticky" elevation={0} sx={{
        background: scrolled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(24px) saturate(1.4)',
        borderBottom: '1px solid',
        borderColor: scrolled ? 'rgba(148,163,184,0.15)' : 'rgba(148,163,184,0.06)',
        transition: 'all 0.35s ease',
      }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: 0.5, minHeight: { xs: 64 } }}>
            {/* Logo */}
            <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mr: 3 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
                boxShadow: '0 4px 12px rgba(79,70,229,0.3)',
              }}>
                <Bolt sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800, fontSize: '1.05rem', display: { xs: 'none', sm: 'block' }, letterSpacing: '-0.02em' }}>
                SpareConnect
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Nav Links */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
              <Button component={Link} to="/marketplace" size="small" sx={navLink('/marketplace')}>Marketplace</Button>
              <Button component={Link} to="/ai-search" size="small" startIcon={<Search sx={{ fontSize: '16px !important' }} />} sx={navLink('/ai-search')}>AI Search</Button>

              {isAuthenticated ? (
                <>
                  <Button component={Link} to="/dashboard" size="small" sx={navLink('/dashboard')}>Dashboard</Button>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1.5, height: 22, my: 'auto', borderColor: 'rgba(148,163,184,0.15)' }} />

                  <Tooltip title="Wishlist" arrow placement="bottom">
                    <IconButton component={Link} to="/wishlist" size="small" sx={{ color: isActive('/wishlist') ? 'error.main' : 'text.secondary', '&:hover': { color: 'error.main' } }}>
                      <FavoriteBorder sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cart" arrow placement="bottom">
                    <IconButton component={Link} to="/cart" size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                      <Badge badgeContent={cartCount} color="primary" max={9} sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16, top: 2, right: 2 } }}>
                        <ShoppingCart sx={{ fontSize: 20 }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Messages" arrow placement="bottom">
                    <IconButton component={Link} to="/messages" size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                      <Chat sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Notifications" arrow placement="bottom">
                    <IconButton component={Link} to="/notifications" size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                      <Badge badgeContent={unreadCount} color="error" max={9} sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16, top: 2, right: 2 } }}>
                        <Notifications sx={{ fontSize: 20 }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>

                  <IconButton onClick={handleMenu} sx={{ ml: 1 }}>
                    <Avatar sx={{
                      width: 34, height: 34, fontSize: '0.8rem',
                      background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
                      boxShadow: '0 2px 8px rgba(79,70,229,0.3)',
                    }}>
                      {user?.full_name?.charAt(0) || 'U'}
                    </Avatar>
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}
                    PaperProps={{ sx: { width: 250, mt: 1.5, borderRadius: 3, border: '1px solid rgba(148,163,184,0.1)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' } }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                    <Box sx={{ px: 2.5, py: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{user?.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{user?.email}</Typography>
                      <Chip label={user?.role} size="small" sx={{ mt: 1, fontSize: '0.7rem' }} color="primary" variant="outlined" />
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => { handleClose(); navigate('/dashboard'); }} sx={{ py: 1.3, mx: 1, borderRadius: 2 }}>
                      <Dashboard sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} /> Dashboard
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); navigate('/orders'); }} sx={{ py: 1.3, mx: 1, borderRadius: 2 }}>
                      <LocalShipping sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} /> My Orders
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); navigate('/upload'); }} sx={{ py: 1.3, mx: 1, borderRadius: 2 }}>
                      <CloudUpload sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} /> Upload Inventory
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem onClick={handleLogout} sx={{ py: 1.3, mx: 1, borderRadius: 2, color: 'error.main' }}>
                      <Logout sx={{ mr: 1.5, fontSize: 18 }} /> Sign Out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1.5, height: 22, my: 'auto', borderColor: 'rgba(148,163,184,0.15)' }} />
                  <Button component={Link} to="/login" size="small" sx={{ color: 'text.secondary', fontWeight: 500, '&:hover': { color: 'primary.main' } }}>
                    Sign In
                  </Button>
                  <Button component={Link} to="/register" variant="contained" size="small" startIcon={<PersonAdd sx={{ fontSize: '15px !important' }} />}>
                    Get Started
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </HideOnScroll>
  );
};

export default Navbar;